import { z } from 'zod';
import { restricted } from './redaction.js';
/**
 * Weekly availability, time off, and the caregiver-client relationships —
 * restrictions and orientation (slice-02 session 7, §6.3.5).
 */
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-10-06 — four digits, two, two.');
/** `HH:MM`, 24-hour. `24:00` is not a time; a window ending at midnight ends at `00:00`. */
const clockTime = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a 24-hour time like 09:00 or 17:30.');
// ---------------------------------------------------------------------------
// Weekly availability
// ---------------------------------------------------------------------------
export const availabilityTypeSchema = z.enum(['AVAILABLE', 'PREFERRED', 'UNAVAILABLE']);
export const availabilityRuleSchema = z.object({
    id: z.string().uuid(),
    /** ISO: 1 = Monday … 7 = Sunday. */
    dayOfWeek: z.number().int().min(1).max(7),
    startTime: clockTime,
    /** At or before `startTime` means it runs past midnight — overnight care is ordinary. */
    endTime: clockTime,
    effectiveFrom: isoDate,
    effectiveTo: isoDate.nullable(),
    type: availabilityTypeSchema,
});
export const createAvailabilityRuleSchema = z
    .object({
    dayOfWeek: z.number().int().min(1).max(7),
    startTime: clockTime,
    endTime: clockTime,
    effectiveFrom: isoDate,
    effectiveTo: isoDate.nullish(),
    type: availabilityTypeSchema.default('AVAILABLE'),
})
    .refine((body) => body.startTime !== body.endTime, {
    path: ['endTime'],
    message: 'A window needs a length. Pick an end time different from the start.',
})
    .refine((body) => !body.effectiveTo || body.effectiveTo >= body.effectiveFrom, {
    path: ['effectiveTo'],
    message: 'The last day can’t be before the first.',
});
export const removeRecordSchema = z.object({
    reason: z.string().trim().max(2_000).nullish(),
});
// ---------------------------------------------------------------------------
// Time off
// ---------------------------------------------------------------------------
export const timeOffTypeSchema = z.enum([
    'VACATION',
    'SICK',
    'PERSONAL',
    'FAMILY',
    'BEREAVEMENT',
    'UNPAID',
    'OTHER',
]);
export const timeOffStatusSchema = z.enum(['REQUESTED', 'APPROVED', 'DENIED', 'CANCELLED']);
export const timeOffRequestSchema = z.object({
    id: z.string().uuid(),
    /** Whole days, inclusive, in the agency's timezone. */
    startDate: isoDate,
    endDate: isoDate,
    type: timeOffTypeSchema,
    status: timeOffStatusSchema,
    note: z.string().nullable(),
    reviewedByName: z.string().nullable(),
    reviewedAt: z.string().nullable(),
    reviewerNote: z.string().nullable(),
    createdAt: z.string(),
});
export const createTimeOffRequestSchema = z
    .object({
    startDate: isoDate,
    endDate: isoDate,
    type: timeOffTypeSchema,
    note: z.string().trim().max(2_000).nullish(),
})
    .refine((body) => body.endDate >= body.startDate, {
    path: ['endDate'],
    message: 'The last day off can’t be before the first.',
});
export const decideTimeOffSchema = z.object({
    decision: z.enum(['APPROVED', 'DENIED']),
    note: z.string().trim().max(2_000).nullish(),
});
/** A span a caregiver is genuinely free, after unavailability and approved time off. */
export const availabilityWindowSchema = z.object({
    date: isoDate,
    startTime: clockTime,
    /** `24:00` here means the window runs to midnight. */
    endTime: z.string(),
    preferred: z.boolean(),
});
export const availabilityQuerySchema = z.object({
    from: isoDate.optional(),
    to: isoDate.optional(),
});
export const caregiverAvailabilitySchema = z.object({
    caregiverId: z.string().uuid(),
    /** The agency's zone; every date and time here is local to it. */
    timeZone: z.string(),
    from: isoDate,
    to: isoDate,
    rules: z.array(availabilityRuleSchema),
    timeOff: z.array(timeOffRequestSchema),
    /** From `resolveAvailability`: what the scheduler will read. */
    windows: z.array(availabilityWindowSchema),
});
// ---------------------------------------------------------------------------
// Restrictions and orientation — shown on both records
// ---------------------------------------------------------------------------
export const restrictionTypeSchema = z.enum(['PREFERRED', 'EXCLUDED']);
/**
 * One pairing, as either record shows it.
 *
 * The **names** are redacted by the permission that governs the other record:
 * HR reads a caregiver's restrictions and holds no `client:read`, so it sees
 * that an exclusion exists and why, not who the client is; a care manager reads
 * a client's and holds no `caregiver:read`. The type and the reason are never
 * redacted — the person who needs to know "do not send this aide" is as often
 * looking at one record as the other.
 */
export const restrictionSchema = z.object({
    id: z.string().uuid(),
    caregiverId: z.string().uuid(),
    clientId: z.string().uuid(),
    caregiverName: restricted(z.string().nullable(), 'caregiver:read'),
    clientName: restricted(z.string().nullable(), 'client:read'),
    type: restrictionTypeSchema,
    reason: z.string(),
    createdByName: z.string().nullable(),
    createdAt: z.string(),
});
export const orientationSchema = z.object({
    id: z.string().uuid(),
    caregiverId: z.string().uuid(),
    clientId: z.string().uuid(),
    caregiverName: restricted(z.string().nullable(), 'caregiver:read'),
    clientName: restricted(z.string().nullable(), 'client:read'),
    completedAt: z.string(),
    completedByName: z.string().nullable(),
    note: z.string().nullable(),
});
export const relationshipsSchema = z.object({
    restrictions: z.array(restrictionSchema),
    orientations: z.array(orientationSchema),
});
/** The minimum an EXCLUDED reason must say. A word is not a reason. */
export const EXCLUDED_REASON_MIN_LENGTH = 10;
export const createRestrictionSchema = z
    .object({
    clientId: z.string().uuid(),
    type: restrictionTypeSchema,
    reason: z.string().trim().max(2_000).default(''),
})
    .refine((body) => body.type !== 'EXCLUDED' || body.reason.length >= EXCLUDED_REASON_MIN_LENGTH, {
    path: ['reason'],
    message: 'Say why this aide must not be sent. Six months from now, an exclusion with no reason looks like a mistake — and gets removed.',
});
export const removeRestrictionSchema = z.object({
    reason: z
        .string()
        .trim()
        .min(EXCLUDED_REASON_MIN_LENGTH, 'Say why it no longer applies. The removal is kept with the reason.')
        .max(2_000),
});
export const createOrientationSchema = z.object({
    clientId: z.string().uuid(),
    /** Defaults to today in the agency's timezone. */
    completedOn: isoDate.optional(),
    note: z.string().trim().max(2_000).nullish(),
});
//# sourceMappingURL=availability.js.map